'use client';

import { useCallback, useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import type { TypedDocumentNode } from '@apollo/client';
import type { LotAnimalEbvFilteredRequestInput } from '@/graphqlClient/types';
import {
  GetLotAnimalEbvsDocument,
  type GetLotAnimalEbvsQuery,
  type GetLotAnimalEbvsQueryVariables,
} from '@/publicSearch/graphql/queries.generated';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';

// The document resolver sends animal IDs in a GET query string. Keep each
// request bounded, including when ALL is selected for a large catalogue.
const ALL_LOTS_BATCH_SIZE = 100;
const ALL_LOTS_CONCURRENCY = 3;
const ALL_SALE_LOTS_QUERY = GetLotAnimalEbvsDocument as TypedDocumentNode<
  GetLotAnimalEbvsQuery,
  GetLotAnimalEbvsQueryVariables
>;

type AllSaleLotsOptions = {
  enabled: boolean;
  societyId: string | number | null | undefined;
  requestInput: Omit<LotAnimalEbvFilteredRequestInput, 'pagination'>;
};

type AllSaleLotsState = {
  requestKey: string;
  loading: boolean;
  data?: GetLotAnimalEbvsQuery;
  previousData?: GetLotAnimalEbvsQuery;
  error?: Error;
};

/** Load every matching lot without exposing a partly loaded catalogue. */
export function useAllSaleLots({
  enabled,
  societyId,
  requestInput,
}: AllSaleLotsOptions) {
  const client = useApolloClient();
  const [retryNumber, setRetryNumber] = useState(0);
  const [state, setState] = useState<AllSaleLotsState>();
  // Content equality avoids restarting batches when settings or filter objects
  // acquire a new reference but still describe the same request.
  const requestKey = JSON.stringify({ requestInput, societyId });
  const retry = useCallback(() => setRetryNumber((value) => value + 1), []);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    const snapshot = JSON.parse(requestKey) as Pick<
      AllSaleLotsOptions,
      'requestInput' | 'societyId'
    >;
    const sortOptions = snapshot.requestInput.sortOptions ?? [];
    // OFFSET pages need a unique order even when many animals share the
    // selected sex, birth date or EBV. Lot numbers are unique within a sale.
    if (!sortOptions.some(({ field }) => field === 'LOT_NO')) {
      snapshot.requestInput.sortOptions = [
        ...sortOptions,
        { field: 'LOT_NO', direction: 'ASCENDING', nullsLast: false },
      ];
    }

    async function loadAllLots() {
      setState((previous) => ({
        requestKey,
        loading: true,
        previousData:
          previous?.requestKey === requestKey ? previous.data : undefined,
      }));

      try {
        const items: GetLotAnimalEbvsQuery['lotAnimalEbvs']['items'] = [];
        const seenLots = new Set<string>();
        let totalCount: number | undefined;

        async function fetchPage(currentPage: number) {
          const result = await client.query({
            query: ALL_SALE_LOTS_QUERY,
            variables: {
              requestInput: {
                ...snapshot.requestInput,
                includeTotalCount: true,
                pagination: {
                  currentPage,
                  pageSize: ALL_LOTS_BATCH_SIZE,
                },
              },
            },
            fetchPolicy: 'no-cache',
            errorPolicy: 'none',
            context: {
              headers: setDefaultHeader(snapshot.societyId),
              fetchOptions: { signal: controller.signal },
              // A new search must not attach to an aborted identical request.
              queryDeduplication: false,
            },
          });
          if (controller.signal.aborted) throw new Error('Search cancelled');

          const page = result.data?.lotAnimalEbvs;
          if (
            !page ||
            !Number.isSafeInteger(page.totalCount) ||
            page.totalCount < 0
          ) {
            throw new Error('Could not load all lots. Please search again.');
          }
          return page;
        }

        function appendPage(page: GetLotAnimalEbvsQuery['lotAnimalEbvs']) {
          if (totalCount !== undefined && totalCount !== page.totalCount) {
            throw new Error(
              'The sale list changed while loading. Please search again.',
            );
          }
          totalCount = page.totalCount;

          for (const item of page.items) {
            const key = `${item.societyId}:${item.saleId}:${item.lotNo}`;
            if (seenLots.has(key)) {
              throw new Error(
                'The sale list changed while loading. Please search again.',
              );
            }
            seenLots.add(key);
            items.push(item);
          }
          if (
            items.length > totalCount ||
            (items.length < totalCount &&
              page.items.length !== ALL_LOTS_BATCH_SIZE)
          ) {
            throw new Error('Could not load all lots. Please search again.');
          }
        }

        const firstPage = await fetchPage(1);
        appendPage(firstPage);
        const totalPages = Math.ceil(
          firstPage.totalCount / ALL_LOTS_BATCH_SIZE,
        );
        for (
          let currentPage = 2;
          currentPage <= totalPages;
          currentPage += ALL_LOTS_CONCURRENCY
        ) {
          const batchCount = Math.min(
            ALL_LOTS_CONCURRENCY,
            totalPages - currentPage + 1,
          );
          // Promise.all retains page order even when later requests finish first.
          const pages = await Promise.all(
            Array.from({ length: batchCount }, (_, index) =>
              fetchPage(currentPage + index),
            ),
          );
          if (controller.signal.aborted) return;
          pages.forEach(appendPage);
        }

        setState({
          requestKey,
          loading: false,
          data: { lotAnimalEbvs: { items, totalCount: firstPage.totalCount } },
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        // Stop sibling requests after one batch fails; never expose partial data.
        controller.abort();
        setState({
          requestKey,
          loading: false,
          error:
            error instanceof Error
              ? error
              : new Error('Could not load all lots. Please search again.'),
        });
      }
    }

    void loadAllLots();
    return () => controller.abort();
  }, [client, enabled, requestKey, retryNumber]);

  // Do not show the previous sale/filter result during the render before its
  // replacement effect starts. The abort guard also rejects late responses.
  const currentState =
    enabled && state?.requestKey === requestKey ? state : undefined;
  return {
    data: currentState?.data,
    previousData: currentState?.previousData,
    error: currentState?.error,
    loading: enabled && (currentState?.loading ?? true),
    retry,
  };
}
